import {FC, useEffect, useMemo, useState} from 'react';
//mui
import {Box, Popover, Typography, Tab, Tabs, Badge} from '@mui/material';
//components
import Button from '@/shared/components/Button';
import NotificationItem from '@/components/Organization/Notifications/NotificationItem';
//constants
import {H6, MARK_ALL_AS_READ, TEXT, PRIMARY, SECONDARY, SUBTITLE2, VIEW_ALL_NOTIFICATIONS} from '@/utils/constants';
//types
import {INotification, INotificationPopup, TabType} from '@/utils/types';
//helpers
import {getGroupedNotificationsByDate} from '@/utils/helpers/notifications';
//icons
import {ReactComponent as ChecksIcon} from '@icons/checks.svg';
//styles
import {notificationPopupStyle} from './OrganizationNotificationPopup.style';
import {
  allNotificationSelector,
  getAllNotifications,
  //   notificationLoaderSelector,
  unreadNotificationSelector,
} from '@/store/notification';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector} from '@/store/organization-event';
import {getUserIdSelector} from '@/store/user';
import {useGetOrganizationId} from '@/utils/hooks';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {getOrganizationSelector} from '@/store/organization';
import {NotificationService} from '@/utils/services/notification.service';
import {useApiInstance} from '@/contexts/ApiInstanceContext';
import {USER_TYPE} from '@/utils/enums';
import {getSuperAdminSelector} from '@/store/admin';

const NotificationPopup: FC<INotificationPopup> = ({id, open, anchorEl, handleClose, handleLinkClick}) => {
  const dispatch = useAppDispatch();

  const [activeTab, setActiveTab] = useState<'all' | 'required'>('required');

  const organizationId = useGetOrganizationId();
  const userId = useAppSelector(getUserIdSelector);
  const organization = useAppSelector(getOrganizationSelector);
  const admin = useAppSelector(getSuperAdminSelector);
  const currentEvent = useAppSelector(getCurrentEventSelector);
  const {notifications: unreadNotifications, totalCount: unreadTotalCount} = useAppSelector(unreadNotificationSelector);
  const {notifications: allNotifications, totalCount: allTotalCount} = useAppSelector(allNotificationSelector);

  const notifications = activeTab === 'required' ? unreadNotifications : allNotifications;

  // console.log('un', unreadNotifications);
  // console.log('all', allNotifications);

  // const hasAnyNotifications = allTotalCount > 0;
  const hasAnyNotifications = allTotalCount > 0 || unreadTotalCount > 0;

  const {apiInstance, role} = useApiInstance();

  const roleId = (() => {
    switch (role) {
      case USER_TYPE.ORGANIZATION:
        return organization?.ownerUserId;
      case USER_TYPE.ADMIN:
        return admin?.id;
      default:
        return userId;
    }
  })();

  const isReadyToFetch = !!roleId && (role === USER_TYPE.ADMIN || !!currentEvent?.id);

  const changeTab = (_: React.SyntheticEvent, newTab: TabType) => {
    setActiveTab(newTab);
  };

  const groupedNotificationsByDate = useMemo(() => getGroupedNotificationsByDate(notifications), [notifications]);
  const handleNotificationClick = async (item: INotification) => {
    if (item.isRead) return;

    try {
      await NotificationService.updateOneNotificationStatus(item.id, apiInstance);
      getNotificationData();

      if (activeTab !== 'required') {
        const data = {
          page: 1,
          pageSize: 10,
          isOrderDesc: true,
          eventId: currentEvent?.id,
          isOrganization: role === USER_TYPE.ORGANIZATION,
          userId: roleId,
          isRead: false,
          ...(role === USER_TYPE.ORGANIZATION && {organizationId}),
        };
        dispatch(getAllNotifications({data, apiInstance})).catch(() => {});
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      if (role === USER_TYPE.USER && userId) {
        await NotificationService.updateAllNotificationsUserStatus(userId);
      } else if (role === USER_TYPE.ORGANIZATION && organizationId && currentEvent?.id) {
        const data = {
          organizationId,
          eventId: currentEvent.id,
          isOrganization: true,
        };
        await NotificationService.updateAllNotificationsOrganizationStatus(data, apiInstance);
      } else if (role === USER_TYPE.ADMIN) {
        const data = {
          userId: admin?.id,
          isOrganization: false,
        };
        await NotificationService.updateAllNotificationsOrganizationStatus(data, apiInstance);
      }

      getNotificationData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const getNotificationData = async () => {
    if (!isReadyToFetch) return;

    // if ((role === USER_TYPE.ORGANIZATION || role === USER_TYPE.USER) && !currentEvent?.id) {
    //   return;
    // }

    try {
      const data = {
        page: 1,
        pageSize: 10,
        isOrderDesc: true,
        ...(currentEvent?.id && {eventId: currentEvent?.id}),
        isOrganization: role === USER_TYPE.ORGANIZATION,
        userId: roleId,
        ...(activeTab === 'required' && {isRead: false}),
        ...(role === USER_TYPE.ORGANIZATION && {organizationId}),
      };

      await dispatch(getAllNotifications({data, apiInstance})).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    if (!roleId) return;
    getNotificationData();
  }, [currentEvent?.id, activeTab, open, roleId]);

  // Первинний "all" фетч — заповнює allNotificationData в redux, звідки й hasAnyNotifications
  useEffect(() => {
    if (!isReadyToFetch) return;

    const data = {
      page: 1,
      pageSize: 1,
      isOrderDesc: true,
      ...(currentEvent?.id && {eventId: currentEvent.id}),
      isOrganization: role === USER_TYPE.ORGANIZATION,
      userId: roleId,
      ...(role === USER_TYPE.ORGANIZATION && {organizationId}),
    };

    dispatch(getAllNotifications({data, apiInstance})).catch((error) => {
      toast.error(getErrorMessage(error));
    });
  }, [currentEvent?.id, roleId]);

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
    >
      <Box sx={notificationPopupStyle.wrapper}>
        <Box sx={notificationPopupStyle.wrapperTitle}>
          <Typography variant={H6} sx={notificationPopupStyle.title}>
            Notifications
          </Typography>
          {activeTab === 'required' && notifications.length > 0 && (
            <Button
              endIcon={<ChecksIcon />}
              title={MARK_ALL_AS_READ}
              variant={TEXT}
              color={PRIMARY}
              disableRipple
              sx={notificationPopupStyle.button}
              onClick={handleMarkAllAsRead}
              disabled={!unreadTotalCount}
            />
          )}
        </Box>
        {hasAnyNotifications ? (
          <>
            <Box sx={notificationPopupStyle.tabsWrapper}>
              <Tabs value={activeTab} onChange={changeTab} indicatorColor={SECONDARY} sx={notificationPopupStyle.tabs}>
                <Tab
                  disableRipple
                  value="required"
                  label={
                    <Box sx={notificationPopupStyle.iconWrapper}>
                      <Typography variant={SUBTITLE2} sx={{display: 'flex', gap: '20px'}}>
                        New Notifications
                        <Badge sx={notificationPopupStyle.badgeIcon} badgeContent={unreadTotalCount} showZero={false} />
                      </Typography>
                    </Box>
                  }
                />
                <Tab
                  disableRipple
                  value="all"
                  label={
                    <Box>
                      <Typography variant={SUBTITLE2}>All notifications </Typography>
                    </Box>
                  }
                />
              </Tabs>
            </Box>
            {notifications.length > 0 ? (
              <Box sx={notificationPopupStyle.wrapperList}>
                {Object.entries(groupedNotificationsByDate).map(([group, items]) => (
                  <Box key={group} sx={notificationPopupStyle.groupWrapper}>
                    <Typography sx={notificationPopupStyle.group}>{group}</Typography>
                    {(items as INotification[]).map((item) => (
                      <NotificationItem key={item.id} notificationItem={item} handleClick={handleNotificationClick} />
                    ))}
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={notificationPopupStyle.wrapperList}>
                <Typography>No new notifications</Typography>
              </Box>
            )}
          </>
        ) : (
          <Box sx={notificationPopupStyle.wrapperList}>
            <Typography>No available notifications</Typography>
          </Box>
        )}
        {notifications.length > 0 && (
          <Button
            title={VIEW_ALL_NOTIFICATIONS}
            variant={TEXT}
            color={PRIMARY}
            disableRipple
            sx={notificationPopupStyle.link}
            onClick={handleLinkClick}
          />
        )}
      </Box>
    </Popover>
  );
};

export default NotificationPopup;
